import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { useLogout } from '../../useLogout';
import { VALIDATION_TYPE } from 'js/hooks/auth/hooks/useValidateSession/constants';
import { useContinueLogin } from 'js/hooks/auth/hooks/useValidateSession/hooks/useContinueLogin';
import { useValidationManager } from 'js/hooks/auth/hooks/useValidateSession/hooks/useValidationManager';
import { useSmartClosePopups } from 'js/components/common/PopupManager';
import { showChangePasswordPopupAction } from '@interlayer/js/app/components/popups/ChangePasswordPopup/actions';

/* core, added isQuiet(57 line) */
export const useValidatePasswordChange = (validationsRef) => {
    const logout = useLogout();
    const dispatch = useDispatch();
    const continueLogin = useContinueLogin();
    const closePopups = useSmartClosePopups();
    const {
        validationStart, validationDone, isValidationInProcess, areAllValidationsDone
    } = useValidationManager(VALIDATION_TYPE.CHANGE_PASSWORD, validationsRef);

    return useCallback((pasLoginResponse, credentials, options) => {
        const { sessionValidationData = {} } = pasLoginResponse?.data || {};

        if (!sessionValidationData.SessionValidationByPasswordChangeData) {
            validationDone();

            return;
        }

        if (isValidationInProcess()) {
            return;
        }

        let ensureLogout = true;
        const { afterRegisterLogin, isAfterLoginWithCredentials } = options || {};
        const { requiredFields = 'newPassword' } = sessionValidationData.SessionValidationByPasswordChangeData[0] || {};
        const showOldPassword = requiredFields
            .split(',')
            .map(field => field.trim())
            .includes('oldPassword');

        validationStart();

        dispatch(showChangePasswordPopupAction({
            id: VALIDATION_TYPE.CHANGE_PASSWORD,
            showOldPassword,
            onSuccess: async () => {
                validationDone();

                if (areAllValidationsDone()) {
                    await continueLogin({ credentials, afterRegisterLogin, isAfterLoginWithCredentials });
                }
                ensureLogout = false;
            },
            onClose: async () => {
                if (ensureLogout) {
                    await logout({ isQuiet: true });

                    closePopups();
                }
            }
        }));
    }, [
        closePopups, logout, dispatch, continueLogin, validationStart,
        validationDone, isValidationInProcess, areAllValidationsDone
    ]);
};
